import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Autocomplete } from '@material-ui/lab'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { CircularProgress, makeStyles, TextField } from '@material-ui/core'
import noop from 'lodash/noop'
import Icon from '../../atoms/Icon'
import { ICON_NAMES } from '../../../constants'
import { fsExcludeClass } from '../../../utils/fsExclude'
import { mapInputStyles } from './styles'

const useStyles = makeStyles((theme) => ({
  inputRoot: {
    minWidth: 'fit-content',
    width: '100%',
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'var(--theme-color-surface-d0)',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    transition: 'outline 200ms ease-in-out',
    outline: '1px solid transparent',
    '&:has(:-internal-autofill-selected)': {
      backgroundColor: 'rgb(232, 240, 254)'
    },
    '&:focus-within': {
      outline: '1px solid var(--color-primary-500)'
    }
  },
  input: mapInputStyles(theme),
  paper: {
    boxShadow: '0px 4px 12px 0px rgba(24, 27, 53, 0.2)'
  },
  sydMenuList: {
    borderRadius: 'var(--border-radius-md)',
    padding: 0,
    minWidth: '300px',
    background: '#fff'
  },
  sydMenuItem: {
    padding: '8px',
    fontSize: '14px',
    '&.Mui-selected': {
      fontWeight: 700,
      background: '#F5F6F7'
    }
  },
  optionSubLabel: {
    color: 'var(--color-neutral-600)',
    fontSize: '12px',
    display: 'block'
  },
  endAdornment: {
    marginRight: '8px'
  },
  chip: {
    margin: '2px',
    padding: '8px',
    maxWidth: '250px',
    backgroundColor: 'var(--color-primary-100)',
    color: 'var(--color-contrast-primary-100)',
    borderRadius: 'var(--border-radius-sm)',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.8rem'
  },
  moreIndicator: {
    margin: '2px',
    padding: '4px 8px',
    backgroundColor: 'var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    color: 'var(--color-contrast-neutral-200)',
    alignItems: 'center'
  }
}))

const SydAutocomplete = forwardRef(function SydAutocomplete ({
  sensitive,
  className,
  name,
  onChange = noop,
  onBlur = noop,
  onInputChange = noop,
  value: _value = [],
  placeholder,
  defaultValue,
  isLoading = false,
  disabled = false,
  size = 'lg',
  options: _options = [],
  multiple = false,
  disableClearable = false,
  maxShow,
  renderOption: customRenderOption
}, ref) {
  const classes = useStyles({ size, disabled })
  const options = Array.isArray(_options) ? _options : []
  const [value, setValue] = useState(defaultValue ?? (multiple ? [] : null))

  const inputRef = useRef(null)
  useImperativeHandle(ref, () => ({
    clear: () => setValue(defaultValue),
    value
  }), [value, defaultValue])

  useEffect(() => {
    if (_value === undefined) return
    if (value === _value) return
    if (
      Array.isArray(_value) &&
      Array.isArray(value) &&
      _value.length === value.length &&
      _value.every((v, i) => {
        const currentValue = value[i]
        const hasValueProp = v && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'value')
        if (hasValueProp) {
          return v.value === currentValue?.value
        }
        return v === currentValue
      })
    ) return
    setValue(_value)
  }, [_value, value, setValue])

  const handleOnInputChange = useCallback((event, value, reason) => {
    if (reason === 'input') {
      onInputChange(event, value)
    }
  }, [onInputChange])

  const handleOnChange = useCallback((e, value) => {
    setValue && setValue(value)
    onChange(e, value)
  }, [onChange])

  return (
    <div>
      <Autocomplete
        ref={inputRef}
        options={options}
        onChange={handleOnChange}
        onInputChange={handleOnInputChange}
        defaultValue={defaultValue}
        clearOnBlur={false}
        placeholder={placeholder}
        disabled={disabled}
        fullWidth
        value={value}
        loading={isLoading}
        multiple={multiple}
        disableClearable={disableClearable}
        noOptionsText={isLoading ? 'Loading...' : 'No options'}
        classes={{
          root: clsx(
            classes.sydSelect,
            { [classes.disabled]: disabled },
            className && className
          ),
          paper: classes.paper,
          listbox: classes.sydMenuList,
          endAdornment: classes.endAdornment
        }}
        renderInput={(params) => (
          <TextField
            placeholder={!value?.length && placeholder}
            {...params}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              classes: {
                input: sensitive ? `${fsExcludeClass} ${classes.input}` : classes.input
              },
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color='primary' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
            classes={{
              root: classes.inputRoot
            }}
          />
        )}
        getOptionLabel={(option) => (option?.label ?? option ?? '').toString()}
        getOptionSelected={(option, value) => (option?.value ?? option) === (value?.value ?? value)}
        renderOption={customRenderOption || ((option) => (
          <div className={clsx(classes.sydMenuItem, { [fsExcludeClass]: sensitive })}>
            {option?.label ?? option}
            {option.subLabel && <span className={classes.optionSubLabel}>{option.subLabel}</span>}
          </div>
        ))}
        filterOptions={(options) => options.filter(opt => opt)}
        popupIcon={<Icon name={ICON_NAMES.chevronDown} customSize='20px' />}
        closeIcon={<Icon name={ICON_NAMES.close} customSize='18px' />}
        renderTags={(value, getTagProps) => {
          if (!maxShow || value.length <= maxShow) {
            return value.map((option, index) => (
              <div
                key={index}
                {...getTagProps({ index })}
                className={classes.chip}
              >
                {option?.label ?? option}
              </div>
            ))
          }

          // Show limited selections + more indicator
          const visibleSelections = value.slice(0, maxShow)
          const remainingCount = value.length - maxShow

          return [
            ...visibleSelections.map((option, index) => (
              <div
                key={index}
                {...getTagProps({ index })}
                className={classes.chip}
              >
                {option?.label ?? option}
              </div>
            )),
            <div key='more-indicator' className={classes.moreIndicator}>+{remainingCount} more</div>
          ]
        }}
      />
    </div>
  )
})

const valueObject = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
})

SydAutocomplete.propTypes = {
  sensitive: PropTypes.bool,
  className: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onInputChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, valueObject, PropTypes.arrayOf(valueObject)]),
  placeholder: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, valueObject, PropTypes.arrayOf(valueObject)]),
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'lg']),
  options: PropTypes.array,
  isLoading: PropTypes.bool,
  multiple: PropTypes.bool,
  disableClearable: PropTypes.bool,
  maxShow: PropTypes.number,
  renderOption: PropTypes.func
}

export default SydAutocomplete
